// Browser-side delivery analysis of a recorded answer (no audio leaves the device for this).
// Returns pace/pause/pitch/energy metrics the backend combines with transcript metrics.

function rmsFrames(data, frame) {
  const out = new Float32Array(Math.floor(data.length / frame));
  for (let f = 0; f < out.length; f++) {
    let s = 0;
    const o = f * frame;
    for (let i = 0; i < frame; i++) s += data[o + i] * data[o + i];
    out[f] = Math.sqrt(s / frame);
  }
  return out;
}

function percentile(sorted, p) {
  if (!sorted.length) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))];
}

function downsample(data, sr, target = 16000) {
  const k = Math.max(1, Math.round(sr / target));
  if (k === 1) return { data, sr };
  const out = new Float32Array(Math.floor(data.length / k));
  for (let i = 0; i < out.length; i++) {
    let s = 0;
    for (let j = 0; j < k; j++) s += data[i * k + j];
    out[i] = s / k;
  }
  return { data: out, sr: sr / k };
}

function pitchAt(data, start, win, sr) {
  const minLag = Math.floor(sr / 400), maxLag = Math.floor(sr / 75);
  if (start + win + maxLag >= data.length) return null;
  let e0 = 0;
  for (let i = 0; i < win; i++) e0 += data[start + i] * data[start + i];
  if (e0 < 1e-6) return null;
  let bestLag = 0, best = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let c = 0, e1 = 0;
    for (let i = 0; i < win; i++) { const b = data[start + i + lag]; c += data[start + i] * b; e1 += b * b; }
    const r = c / Math.sqrt(e0 * e1 + 1e-9);
    if (r > best) { best = r; bestLag = lag; }
  }
  return best > 0.55 && bestLag ? sr / bestLag : null;
}

export async function analyzeAnswerAudio(blob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ac = new AudioCtx();
  try {
    const decoded = await ac.decodeAudioData(await blob.arrayBuffer());
    const raw = decoded.getChannelData(0);
    const duration_s = decoded.duration;
    const { data, sr } = downsample(raw, decoded.sampleRate);
    const frame = Math.round(sr * 0.03);
    const rms = rmsFrames(data, frame);
    const sorted = Array.from(rms).sort((a, b) => a - b);
    const floor = percentile(sorted, 0.2), p95 = percentile(sorted, 0.95);
    const thresh = Math.max(0.006, floor * 3, p95 * 0.12);
    const voiced = Array.from(rms, (v) => v > thresh);
    const first = voiced.indexOf(true), last = voiced.lastIndexOf(true);
    let pauses = 0, longest = 0, run = 0, speechFrames = 0;
    for (let f = first; f <= last && f >= 0; f++) {
      if (voiced[f]) { speechFrames++; if (run * 0.03 > 0.7) { pauses++; longest = Math.max(longest, run * 0.03); } run = 0; }
      else run++;
    }
    const win = Math.round(sr * 0.04);
    const f0s = [];
    for (let f = Math.max(0, first); f <= last; f += 3) {
      if (!voiced[f]) continue;
      const p = pitchAt(data, f * frame, win, sr);
      if (p) f0s.push(p);
    }
    let pitch_cv = null, pitch_range_st = null;
    if (f0s.length >= 8) {
      const s = [...f0s].sort((a, b) => a - b);
      const med = s[Math.floor(s.length / 2)];
      const clean = s.filter((v) => v > med * 0.5 && v < med * 2);
      const mean = clean.reduce((a, b) => a + b, 0) / clean.length;
      const sd = Math.sqrt(clean.reduce((a, b) => a + (b - mean) ** 2, 0) / clean.length);
      pitch_cv = sd / mean;
      pitch_range_st = 12 * Math.log2(percentile(clean, 0.9) / Math.max(1, percentile(clean, 0.1)));
    }
    const vr = Array.from(rms).filter((v, i) => voiced[i]);
    let energy_cv = null;
    if (vr.length > 5) {
      const mean = vr.reduce((a, b) => a + b, 0) / vr.length;
      energy_cv = Math.sqrt(vr.reduce((a, b) => a + (b - mean) ** 2, 0) / vr.length) / mean;
    }
    return {
      duration_s, speech_s: speechFrames * 0.03, pauses, longest_pause_s: longest,
      pitch_cv, pitch_range_st, energy_cv, voiced_ratio: duration_s ? (speechFrames * 0.03) / duration_s : null,
    };
  } catch {
    return null;
  } finally {
    try { ac.close(); } catch { /* noop */ }
  }
}
